/**
 * Integrations Hook
 *
 * Manages workspace-level integrations (HubSpot, Salesforce, Stripe, etc.).
 * Supports both preview mode (dummy data) and production mode (real backend data).
 *
 * For user-level integrations (Google, Microsoft, Slack, Zoom), see use-accounts.ts.
 */

import { useCallback, useMemo } from 'react';
import { isLovablePreview } from '@/lib/utils/environment';
import { useWorkspaceId } from './use-workspace-id';
import {
  useGetIntegrationsQuery,
  useDisconnectIntegrationMutation,
  useCrmIngestMutation,
  IntegrationPlatform,
  IntegrationStatus,
  CrmIngestionPlatform,
  DisconnectReason,
} from '@/lib/graphql/types-and-hooks';

export interface WorkspaceIntegration {
  name: string;
  platform: IntegrationPlatform;
  connected: boolean;
  // True when the integration was unexpectedly disconnected (token expired /
  // auth revoked) and needs the user to reconnect. False for deliberate
  // disconnects and never-connected integrations.
  needsReconnect: boolean;
  category: string;
  addedTs?: string;
}

const INTEGRATION_CONFIGS: Record<string, { name: string; category: string }> = {
  [IntegrationPlatform.HubspotMain]: { name: 'HubSpot', category: 'CRM' },
  [IntegrationPlatform.SalesforceMain]: { name: 'Salesforce', category: 'CRM' },
  [IntegrationPlatform.Zapier]: { name: 'Zapier', category: 'Automation' },
  [IntegrationPlatform.StripeConnect]: { name: 'Stripe', category: 'Payment' },
  [IntegrationPlatform.Quickbooks]: { name: 'QuickBooks', category: 'Accounting' },
  [IntegrationPlatform.Mailchimp]: { name: 'Mailchimp', category: 'Marketing' },
  // Gong is a workspace integration like the rest. Omitting it here did not
  // merely hide a badge: productionIntegrations is built by mapping over these
  // entries, so Gong never reached allIntegrations and isConnected('Gong')
  // returned false however ACTIVE the backend reported it. That made
  // SettingsPage's already-connected branch unreachable, and with it the dialog
  // holding the per-workspace Gong webhook URL — the URL a customer must paste
  // into a Gong Automation Rule for calls to sync continuously.
  [IntegrationPlatform.Gong]: { name: 'Gong', category: 'Call Recorder' },
  // Fireflies' workspace-wide connection (an admin's Fireflies admin key). The
  // per-user Fireflies and Granola connections are UserIntegrationConfig rows,
  // read via userIntegrations — not listed here. Fireflies syncs through
  // syncFirefliesWorkspace, not crmIngest, so it has no PLATFORM_TO_CRM_INGESTION
  // entry.
  [IntegrationPlatform.Fireflies]: { name: 'Fireflies.ai', category: 'Call Recorder' },
};

// Platforms excluded from display (handled elsewhere or superseded)
const EXCLUDED_PLATFORMS = new Set([
  IntegrationPlatform.Zoom,
  IntegrationPlatform.GoogleCalendar,
  IntegrationPlatform.ManualCsv,
  IntegrationPlatform.Salesforce,   // superseded by SalesforceMain
  IntegrationPlatform.Hubspot,      // superseded by HubspotMain
  IntegrationPlatform.Coffee,
  IntegrationPlatform.Microsoft,
  IntegrationPlatform.Slack,
]);

// Map display names back to platforms for API calls
const NAME_TO_PLATFORM: Record<string, IntegrationPlatform> = Object.fromEntries(
  Object.entries(INTEGRATION_CONFIGS).map(([platform, config]) => [config.name, platform as IntegrationPlatform])
);

// Map IntegrationPlatform to CrmIngestionPlatform for sync mutations
const PLATFORM_TO_CRM_INGESTION: Record<string, CrmIngestionPlatform> = {
  [IntegrationPlatform.HubspotMain]: CrmIngestionPlatform.HubspotMain,
  [IntegrationPlatform.SalesforceMain]: CrmIngestionPlatform.SalesforceMain,
  [IntegrationPlatform.StripeConnect]: CrmIngestionPlatform.StripeConnect,
  [IntegrationPlatform.Quickbooks]: CrmIngestionPlatform.Quickbooks,
  [IntegrationPlatform.Mailchimp]: CrmIngestionPlatform.Mailchimp,
  [IntegrationPlatform.Zapier]: CrmIngestionPlatform.Zapier,
  // Without this entry triggerSync('Gong') returned "Sync not supported" before
  // sending anything, so the Gong card's Sync button only ever toasted
  // "Failed to sync Gong". The backend crmIngest mutation handles GONG.
  [IntegrationPlatform.Gong]: CrmIngestionPlatform.Gong,
};

// Legacy platform remapping: when disconnecting, use the "Main" variant
const DISCONNECT_PLATFORM_REMAP: Record<string, string> = {
  SALESFORCE: 'SALESFORCE_MAIN',
  HUBSPOT: 'HUBSPOT_MAIN',
};

export function useIntegrations() {
  const preview = isLovablePreview();
  const workspaceId = useWorkspaceId();

  const { data, loading, refetch } = useGetIntegrationsQuery({
    skip: preview || !workspaceId,
    variables: {
      workspaceId: workspaceId ? parseInt(workspaceId) : 0,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [disconnectMutation] = useDisconnectIntegrationMutation();
  const [crmIngestMutation] = useCrmIngestMutation();

  // Preview mode dummy data
  const previewIntegrations: WorkspaceIntegration[] = useMemo(() => {
    if (!preview) return [];
    return [
      { name: 'QuickBooks', platform: IntegrationPlatform.Quickbooks, connected: true, needsReconnect: false, category: 'Accounting' },
      { name: 'Mailchimp', platform: IntegrationPlatform.Mailchimp, connected: false, needsReconnect: false, category: 'Marketing' },
    ];
  }, [preview]);

  // Production mode: map backend data
  const productionIntegrations: WorkspaceIntegration[] = useMemo(() => {
    if (preview) return [];

    const integrations = data?.integrations || [];
    const activeSet = new Set<string>();

    // Track which platforms are active
    for (const integration of integrations) {
      if (integration && integration.status === IntegrationStatus.Active && !EXCLUDED_PLATFORMS.has(integration.platform)) {
        activeSet.add(integration.platform);
      }
    }

    // Build full list: all configured platforms with their connected status
    return Object.entries(INTEGRATION_CONFIGS).map(([platform, config]) => {
      const row = integrations.find(i => i?.platform === platform);
      return {
        name: config.name,
        platform: platform as IntegrationPlatform,
        connected: activeSet.has(platform),
        // Unexpected disconnect: inactive row flagged EXPIRED by the backend.
        needsReconnect:
          row?.status === IntegrationStatus.Inactive &&
          row?.disconnectReason === DisconnectReason.Expired,
        category: config.category,
        addedTs: row?.addedTs || undefined,
      };
    });
  }, [preview, data]);

  const allIntegrations = preview ? previewIntegrations : productionIntegrations;
  const connectedApps = useMemo(() => allIntegrations.filter(i => i.connected), [allIntegrations]);
  const availableApps = useMemo(() => allIntegrations.filter(i => !i.connected), [allIntegrations]);

  // Get connected status by display name
  const isConnected = useCallback((name: string): boolean => {
    return allIntegrations.some(i => i.name === name && i.connected);
  }, [allIntegrations]);

  // Disconnect integration
  const disconnectIntegration = useCallback(async (name: string) => {
    if (preview) return { success: true };
    if (!workspaceId) return { success: false, error: 'No workspace found' };

    const platform = NAME_TO_PLATFORM[name];
    if (!platform) return { success: false, error: `Unknown platform: ${name}` };

    // Apply legacy platform remapping
    const platformValue = platform as string;
    const remappedPlatform = DISCONNECT_PLATFORM_REMAP[platformValue] || platformValue;

    try {
      const result = await disconnectMutation({
        variables: {
          workspaceId: parseInt(workspaceId),
          platform: remappedPlatform,
        },
      });

      if (result.data?.disconnectIntegration?.ok) {
        await refetch({ workspaceId: parseInt(workspaceId) });
        return { success: true };
      }
      return { success: false, error: 'Failed to disconnect integration' };
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      return { success: false, error: String(error) };
    }
  }, [preview, workspaceId, disconnectMutation, refetch]);

  // Trigger sync
  const triggerSync = useCallback(async (name: string) => {
    if (preview) return { success: true };
    if (!workspaceId) return { success: false, error: 'No workspace found' };

    const platform = NAME_TO_PLATFORM[name];
    if (!platform) return { success: false, error: `Unknown platform: ${name}` };

    const crmPlatform = PLATFORM_TO_CRM_INGESTION[platform];
    if (!crmPlatform) return { success: false, error: `Sync not supported for: ${name}` };

    try {
      await crmIngestMutation({
        variables: {
          workspaceId: parseInt(workspaceId),
          platform: crmPlatform,
        },
      });
      await refetch({ workspaceId: parseInt(workspaceId) });
      return { success: true };
    } catch (error) {
      console.error('Error triggering sync:', error);
      return { success: false, error: String(error) };
    }
  }, [preview, workspaceId, crmIngestMutation, refetch]);

  return {
    connectedApps,
    availableApps,
    allIntegrations,
    isConnected,
    loading: !preview && loading,
    // True once the integrations list has loaded at least once (or there is
    // nothing to load). Unlike `loading`, it stays true during a background
    // refetch, so callers can gate a first render on it without flashing.
    loaded: preview || data !== undefined || !loading,
    disconnectIntegration,
    triggerSync,
    refetch,
  };
}
